import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import { TrainerContext } from '../../context/TrainerContext';
import TrainerLayout from './TrainerLayout';
import SearchBar from '../../components/commons/searchbar/SearchBar';
import '../../styles/Podopieczni.css';

const Podopieczni = () => {
  const { trainer } = useContext(TrainerContext);
  const navigate = useNavigate();

  const [dogs, setDogs] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!trainer || !trainer.schoolId) return;

    const fetchDogs = async () => {
      try {
        setLoading(true);
        let endpoint = `/api/v1/dog/${trainer.schoolId}?page=${currentPage}&size=${pageSize}`;

        if (searchTerm.trim()) {
          endpoint += `&search=${encodeURIComponent(searchTerm.trim())}`;
          setIsSearching(true);
        } else {
          setIsSearching(false);
        }

        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` },
        });

        setDogs(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      } catch (err) {
        setError('Nie udało się pobrać psów.');
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(() => {
      fetchDogs();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [trainer, currentPage, pageSize, searchTerm]);

  return (
      <TrainerLayout pageTitle="Twoi podopieczni">
        <div className="podopieczni-page-container">
          <h1 className="podopieczni-mobile-title">Podopieczni</h1>

          <div className="podopieczni-search-section">
            <SearchBar
                width="400px"
                value={searchTerm}
                onChange={setSearchTerm}
                onSearch={() => setCurrentPage(0)}
                placeholder="Szukaj po imieniu pupila lub rasie"
            />

            <div className="podopieczni-pagination-controls-inline">
              <label className="podopieczni-page-size-label">Na stronę:</label>
              <select
                  className="podopieczni-page-size-select"
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>
          </div>

          {loading ? (
              <p className="podopieczni-loading-text">Ładowanie...</p>
          ) : error ? (
              <p className="podopieczni-error-message">{error}</p>
          ) : (
              <>
                {isSearching && dogs.length > 0 && (
                    <div className="podopieczni-search-results-info">
                      Znaleziono: {dogs.length} {dogs.length === 1 ? 'wynik' :
                        dogs.length < 5 ? 'wyniki' : 'wyników'}
                    </div>
                )}

                <div className="podopieczni-table-section">
                  <table className="podopieczni-dogs-table">
                    <thead>
                    <tr>
                      <th>Imię</th>
                      <th>Rasa</th>
                      <th>Wiek</th>
                      <th>Płeć</th>
                      <th>Imię właściciela</th>
                    </tr>
                    </thead>
                    <tbody>
                    {dogs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="podopieczni-no-data-row">
                            {isSearching
                                ? 'Brak wyników wyszukiwania'
                                : 'Brak podopiecznych'}
                          </td>
                        </tr>
                    ) : (
                        dogs.map((dog) => (
                            <tr
                                key={dog.id}
                                onClick={() => navigate(`/dog/details/${dog.id}`)}
                                className="podopieczni-clickable-row"
                            >
                              <td className="podopieczni-centered-column"><strong>{dog.name}</strong></td>
                              <td className="podopieczni-centered-column">{dog.breed}</td>
                              <td className="podopieczni-centered-column">{dog.age}</td>
                              <td className={`podopieczni-gender-text ${dog.gender === "FEMALE" ? "podopieczni-gender-samica" : "podopieczni-gender-samiec"}`}>
                                {dog.gender === "FEMALE" ? "samica" : "samiec"}
                              </td>
                              <td className="podopieczni-centered-column">{dog.ownerName}</td>
                            </tr>
                        ))
                    )}
                    </tbody>
                  </table>
                </div>

                <div className="podopieczni-mobile-cards">
                  {dogs.length === 0 ? (
                      <div className="podopieczni-mobile-no-data">
                        {isSearching ? 'Brak wyników wyszukiwania' : 'Brak podopiecznych'}
                      </div>
                  ) : (
                      dogs.map((dog) => (
                          <div
                              key={dog.id}
                              className="podopieczni-dog-card"
                              onClick={() => navigate(`/dog/details/${dog.id}`)}
                          >
                            <div className="podopieczni-dog-card-header">
                              <div className="podopieczni-dog-name-section">
                                <h3 className="podopieczni-dog-name">{dog.name}</h3>
                                <p className="podopieczni-dog-breed">{dog.breed}</p>
                              </div>
                              <span className={`podopieczni-dog-gender-mobile ${dog.gender === "FEMALE" ? "podopieczni-gender-samica" : "podopieczni-gender-samiec"}`}>
                                                {dog.gender === "FEMALE" ? "samica" : "samiec"}
                                            </span>
                            </div>

                            <div className="podopieczni-dog-details-divider"></div>

                            <div className="podopieczni-dog-details">
                              <div className="podopieczni-dog-detail-item">
                                <span className="podopieczni-dog-detail-label">Wiek</span>
                                <span className="podopieczni-dog-detail-value">{dog.age}</span>
                              </div>
                              <div className="podopieczni-dog-detail-item">
                                <span className="podopieczni-dog-detail-label">Właściciel</span>
                                <span className="podopieczni-dog-detail-value">{dog.ownerName}</span>
                              </div>
                            </div>
                          </div>
                      ))
                  )}
                </div>

                <div className="podopieczni-pagination-section">
                  <button
                      className="podopieczni-pagination-btn"
                      disabled={currentPage === 0}
                      onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    Poprzednia
                  </button>
                  <span className="podopieczni-page-info">
                                Strona {currentPage + 1} z {totalPages}
                            </span>
                  <button
                      className="podopieczni-pagination-btn"
                      disabled={currentPage + 1 >= totalPages}
                      onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Następna
                  </button>
                </div>
              </>
          )}
        </div>
      </TrainerLayout>
  );
};

export default Podopieczni;